import React, { Component } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@trevor/ui-components';
import { getUniques, updateArrayElement } from '@/util/helpers/array-helpers';
import { validateEmail } from '@/util/helpers/string-helpers';
import flex from '../css/module/flex.css';
import InputWithFocus from './InputWithFocus';
import style from '../css/module/team.css';
import credStyle from '../css/module/credentials.css';
import { ADMIN, COLLABORATOR } from '../constants';

const VALID = 'valid';
const DUPLICATE = 'duplicate';
const INVALID = 'invalid';
const TEMPLATE = { emailAddress: '', role: ADMIN, status: INVALID };

class InviteCollaborators extends Component {
  constructor(props) {
    super(props);
    this.state = {
      emails: [TEMPLATE], // an array of objects containing an emailAddress, role, and status
      showPermissions: false,
    };
    this.updateState = this.updateState.bind(this);
    this.inputs = this.inputs.bind(this);
    this.sendInvitations = this.sendInvitations.bind(this);
    this.startAgain = this.startAgain.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  inputs() {
    const { emails } = this.state;
    const { invitationsSent, selfHosted } = this.props;

    return emails.map((email, i) => {
      const newEmail = email.emailAddress;
      const { role } = emails;

      if (newEmail.length === 0 && invitationsSent) {
        return null;
      }

      return (
        <div
          key={i}
          className={`${flex.alignItemsCenter} ${style.inputContainer} ${style.addEmailContainer}`}
        >
          <InputWithFocus
            disableFocus={i > 0}
            content={newEmail}
            onChange={(value) => this.addEmail(value, i)}
            placeholder="name@example.com"
            disabled={invitationsSent || this.props.saving}
          />
          <select
            disabled={invitationsSent}
            value={role}
            onChange={(e) => this.updateRole(e.currentTarget.value, i)}
          >
            <option value={ADMIN}>admin</option>
            <option value={COLLABORATOR}>collaborator</option>
          </select>
          {emails.length === 1 || invitationsSent ? null : (
            <div className={style.remove} onClick={() => this.remove(i)}>
              <span>&times;</span>
            </div>
          )}
          {invitationsSent && email.status === VALID ? (
            <div className={flex.alignItemsCenter}>
              <FontAwesomeIcon
                icon="check-circle"
                style={{
                  fontSize: '1.4em',
                  marginRight: '10px',
                  color: '5fba7d',
                }}
              />
              <b>{selfHosted ? 'Added' : 'Invite sent!'}</b>
            </div>
          ) : null}
          {invitationsSent && email.status === DUPLICATE ? (
            <div className={flex.alignItemsCenter}>
              <FontAwesomeIcon
                icon={['far', 'user']}
                style={{ fontSize: '1.4em', marginRight: '10px' }}
              />
              <b>Already a user</b>
            </div>
          ) : null}
          {invitationsSent && email.status === INVALID ? (
            <div className={flex.alignItemsCenter}>
              <FontAwesomeIcon
                icon="bomb"
                style={{ fontSize: '1.4em', marginRight: '10px' }}
              />
              <b>Invalid email</b>
            </div>
          ) : null}
        </div>
      );
    });
  }

  updateState(newValue) {
    this.setState({
      emails: newValue,
    });
  }

  remove(i) {
    const { emails } = this.state;
    const copyOfArray = emails.slice();
    copyOfArray.splice(i, 1);
    this.updateState(copyOfArray);
  }

  addEmail(value, i) {
    const { emails } = this.state;
    const toUpdate = { ...emails[i] };
    toUpdate.emailAddress = value.trim().toLowerCase();
    toUpdate.status = this.emailStatus(toUpdate.emailAddress);
    const copyOfArray = updateArrayElement(emails, i, toUpdate);
    if (i === emails.length - 1 && validateEmail(toUpdate.emailAddress)) {
      copyOfArray.push(TEMPLATE);
    }
    this.updateState(copyOfArray);
  }

  updateRole(value, i) {
    const { emails } = this.state;
    const toUpdate = { ...emails[i] };
    toUpdate.role = value;
    const copyOfArray = updateArrayElement(emails, i, toUpdate);
    this.updateState(copyOfArray);
  }

  emailStatus(email) {
    const { existingEmails } = this.props;
    if (existingEmails.includes(email)) {
      return DUPLICATE;
    }
    if (validateEmail(email)) {
      return VALID;
    }
    return INVALID;
  }

  validEmails() {
    const { emails } = this.state;
    const validEmails = emails.filter((email) => email.status === VALID);
    // remove any duplicates
    const emailList = getUniques(
      validEmails,
      (email) => email.emailAddress,
      true,
    );
    return emailList;
  }

  sendInvitations() {
    const { onClickSubmit } = this.props;
    onClickSubmit(this.validEmails());
  }

  startAgain() {
    const { startAgain } = this.props;
    this.updateState([TEMPLATE]);
    startAgain();
  }

  handleKeyDown(e) {
    const disabled =
      this.state.emails.filter(
        (email) => email.status === VALID || email.status === DUPLICATE,
      ).length === 0 || this.props.saving;
    if (!disabled && e.keyCode === 13) {
      // enter key pressed
      this.sendInvitations();
    }
  }

  render() {
    const { invitationsSent, saving, selfHosted } = this.props;

    const pulsateClass = this.validEmails().length > 0 ? credStyle.pulsate : '';

    const iconStyle = { marginLeft: '5px', fontSize: '1.1em' };

    return (
      <div>
        <p>
          <b>Invite members</b>
        </p>
        <div className={style.inviteContainer}>
          {this.inputs()}
          <div
            className={`${flex.justifyContentBetween} margin-top-20 ${flex.alignItemsCenter}`}
          >
            <div className={flex.column}>
              <span
                className={`${flex.alignItemsCenter} cursor-pointer`}
                onClick={() =>
                  this.setState({
                    showPermissions: !this.state.showPermissions,
                  })
                }
              >
                Learn about permissions
                <FontAwesomeIcon
                  icon={
                    this.state.showPermissions ? 'caret-down' : 'caret-right'
                  }
                  style={iconStyle}
                />
              </span>
            </div>
            <div>
              {!invitationsSent ? (
                <Button
                  className={pulsateClass}
                  onClick={this.sendInvitations}
                  disabled={
                    this.state.emails.filter(
                      (email) =>
                        email.status === VALID || email.status === DUPLICATE,
                    ).length === 0 || saving
                  }
                >
                  Send invitations
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  onClick={this.startAgain}
                  className={pulsateClass}
                >
                  All done! Clear invites
                </Button>
              )}
            </div>
          </div>
          {!this.state.showPermissions ? null : (
            <div className="margin-top-20">
              <p>
                <b>Collaborators</b>
              </p>
              <p>
                Collaborators can access this datasource and use Trevor.io's
                main features and functionality. This includes:&nbsp;
              </p>
              <ul>
                <li>querying your data,</li>
                <li>creating dashboards,</li>
                <li>setting up data alerts,</li>
                <li>downloading or exporting results.</li>
              </ul>
              <p>
                Collaborators cannot see your database credentials or modify
                your underlying data in any way. You can revoke access any time.
              </p>
              <p className="margin-top-20">
                <b>Admins</b>
              </p>
              <p>Admins have the same rights as Collaborators, but can also:</p>
              <ul>
                <li>invite other users,</li>
                {selfHosted ? null : <li>manage billing,</li>}
                {selfHosted ? null : <li>configure table access and joins,</li>}
                <li>
                  control the Feature Access settings on the Settings page.
                </li>
              </ul>
              <p className="margin-top-20">
                <b>Owner</b>
              </p>
              <p>
                The datasource owner is the only user who can update the
                datasource credentials.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default InviteCollaborators;
